'use client';

import type { CompanyRevenueHistoryPoint } from '@/scripts/company-data';

import { useState } from 'react';
import dynamic from 'next/dynamic';
import { usePathname } from 'next/navigation';
import type { GuvFlatRow } from '@/new_components/guv/guvHistoryService';
import { useTranslation } from 'react-i18next';
import { trackSignupAndRedirect } from '@/lib/track-signup-click';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';

const ChartSkeleton = () => <div className="h-[315px]" />;
const GatedChartSkeleton = () => <div className="h-[206px]" />;

const SalaryTrendChart = dynamic(
  () => import('./SalaryTrendChart').then((mod) => mod.SalaryTrendChart),
  { ssr: false, loading: ChartSkeleton },
);

const CompanyFinancialsIndexChart = dynamic(
  () =>
    import('./CompanyFinancialsIndexChart').then(
      (mod) => mod.CompanyFinancialsIndexChart,
    ),
  { ssr: false, loading: GatedChartSkeleton },
);

const CompanyCostsHistoryChart = dynamic(
  () =>
    import('./CompanyCostsHistoryChart').then(
      (mod) => mod.CompanyCostsHistoryChart,
    ),
  { ssr: false, loading: () => <div className="h-[206px]" /> },
);

// Was a structural copy of CompanyRevenueHistoryPoint, which meant every
// field added there had to be added here too or the call sites stopped
// type-checking. This card only reads the numeric series, so it takes the
// shared type directly.
type RevenueHistoryRow = CompanyRevenueHistoryPoint;

/**
 * Right-column tabbed card with three time-series views: Umsatz, Kosten, Gehalt.
 *
 * Only mounted for authenticated users with ≥ 5 merged GuV rows. Gehalt tab is
 * only shown when > 4 years have both employee count and personnel cost data
 * (mirroring the internal logic of {@link SalaryTrendChart}).
 *
 * For guests, pass `isAuthenticated={false}` to render the sign-in gate instead.
 */
export function ZeitverlaufTabbedCard({
  mergedGuvData,
  revenueHistory,
  isAuthenticated,
  euId,
  className,
}: {
  mergedGuvData: GuvFlatRow[];
  revenueHistory: RevenueHistoryRow[];
  isAuthenticated: boolean;
  euId?: string;
  /** Layout classes from the page (mobile `order-*`, borders) — see MOBILE_ORDER. */
  className?: string;
}) {
  const { t } = useTranslation();
  const pathname = usePathname();
  const [activeZeitverlaufTab, setActiveZeitverlaufTab] = useState('umsatz');

  if (!isAuthenticated) {
    return (
      <Card
        variant="grid"
        className={cn(
          'flex flex-col items-center justify-center p-8 text-center bg-background',
          className,
        )}
        data-nosnippet
      >
        <div className="flex flex-col items-center gap-4">
          <div className="space-y-1 text-center">
            <CardTitle as="h2" className="text-lg">
              {t('companyDetail.zeitverlauf.title', {
                defaultValue: 'Zeitverlauf',
              })}
            </CardTitle>
            <p className="text-sm text-muted-foreground max-w-[240px] mx-auto">
              {t('companyDetail.gated.zeitverlauf', {
                defaultValue:
                  'Melden Sie sich an, um die detaillierte Entwicklung des Gehalts und Kosten einzusehen.',
              })}
            </p>
          </div>
          {/* onClick navigation (not <a href>) so crawlers don't discover
              per-company /auth/login?returnTo= URLs — see GSC "Blocked by
              robots.txt" noise */}
          <Button
            className="mt-2"
            onClick={() =>
              trackSignupAndRedirect(pathname, 'zeitverlauf', euId)
            }
          >
            {t('companyDetail.signInToView')}
          </Button>
        </div>
      </Card>
    );
  }

  if (mergedGuvData.length <= 4) return null;

  // Gehalt tab visibility: mirror SalaryTrendChart — need > 4 years where
  // both employee count and personnel cost exist
  const employeeYears = new Set(
    revenueHistory
      .filter((r) => r.totalEmployees != null && r.totalEmployees > 0)
      .map((r) => r.year),
  );
  const hasGehalt = (() => {
    if (employeeYears.size === 0) return false;
    const guvYears = Array.from(new Set(mergedGuvData.map((d) => d.year)))
      .sort((a, b) => a - b)
      .slice(-8);
    const personnelKeywords = ['personalaufwand', 'loehne und gehaelter'];
    let validYears = 0;
    for (const year of guvYears) {
      if (!employeeYears.has(year)) continue;
      const yearGuv = mergedGuvData.filter((d) => d.year === year);
      const hasPersonnel = yearGuv.some((d) => {
        const text = [
          d.section,
          d.subsection,
          d.subsubsection,
          d.subsubsubsection,
          d.title,
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        return personnelKeywords.some((kw) => text.includes(kw));
      });
      if (hasPersonnel) validYears++;
    }
    return validYears > 4;
  })();
  const tabCount = hasGehalt ? 3 : 2;

  return (
    <Card variant="grid" className={cn('h-[410px]', className)}>
      <CardHeader>
        <CardTitle as="h2">
          {t('companyDetail.zeitverlauf.title', {
            defaultValue: 'Zeitverlauf',
          })}
        </CardTitle>
      </CardHeader>
      <CardContent className="pb-3 flex flex-col flex-1">
        <Tabs
          value={activeZeitverlaufTab}
          onValueChange={setActiveZeitverlaufTab}
          className="w-full"
        >
          <TabsList
            className={cn(
              'grid w-full bg-muted/50 p-1',
              tabCount === 3 ? 'grid-cols-3' : 'grid-cols-2',
            )}
          >
            <TabsTrigger value="umsatz" className="text-xs">
              {t('companyDetail.zeitverlauf.umsatz', {
                defaultValue: 'Umsatz',
              })}
            </TabsTrigger>
            <TabsTrigger value="kosten" className="text-xs">
              {t('companyDetail.zeitverlauf.kosten', {
                defaultValue: 'Kosten',
              })}
            </TabsTrigger>
            {hasGehalt && (
              <TabsTrigger value="gehalt" className="text-xs">
                {t('companyDetail.zeitverlauf.gehalt', {
                  defaultValue: 'Gehalt',
                })}
              </TabsTrigger>
            )}
          </TabsList>
        </Tabs>
        <div className="mt-2 flex flex-col flex-1">
          {activeZeitverlaufTab === 'umsatz' &&
            (mergedGuvData.length > 20 ? (
              <CompanyFinancialsIndexChart
                revenueHistory={revenueHistory}
                guvData={mergedGuvData}
                embedded
              />
            ) : (
              <p className="text-xs text-muted-foreground text-center py-8">
                {t('companyDetail.zeitverlauf.noData', {
                  defaultValue: 'Nicht genügend Daten verfügbar.',
                })}
              </p>
            ))}
          {activeZeitverlaufTab === 'kosten' &&
            (mergedGuvData.length > 20 ? (
              <CompanyCostsHistoryChart
                guvData={mergedGuvData}
                revenueHistory={revenueHistory}
                embedded
              />
            ) : (
              <p className="text-xs text-muted-foreground text-center py-8">
                {t('companyDetail.zeitverlauf.noData', {
                  defaultValue: 'Nicht genügend Daten verfügbar.',
                })}
              </p>
            ))}
          {activeZeitverlaufTab === 'gehalt' && hasGehalt && (
            <SalaryTrendChart
              guvData={mergedGuvData}
              revenueHistory={revenueHistory}
              embedded
            />
          )}
        </div>
      </CardContent>
    </Card>
  );
}
